import {
  ReactElement,
  ChangeEvent,
  MouseEvent,
  useMemo,
  useCallback,
} from 'react';
import {
  DialogTitle,
  DialogContent,
  Stack,
  Avatar,
  FormGroup,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  ToggleButton,
  FormHelperText,
  DialogActions,
  Button,
  createFilterOptions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckFast as VehicleIcon } from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useFormik, FormikHelpers } from 'formik';
import moment from 'moment-timezone';
import { find, lowerFirst } from 'lodash';

import { ScheduleRouteWeeklyDayIndex } from '~services/scheduleRoute/types';
import { updateScheduleRoute } from '~services/scheduleRoute';
import { useUtcOffset } from '~hooks/useUtcOffset';
import { useLazyRequest } from '~hooks/useLazyRequest';
import CustomAutocomplete from '~components/CustomAutocomplete';
import UserInfo from '~components/UserInfo';
import VehicleInfo from '~components/VehicleInfo';
import IsAvailableChip from '~components/IsAvailableChip';
import { formatId, formatterDate } from '~utils/formatter';
import { hasError, getHelperText } from '~utils/formHelpers';
import { getDefaultAvatar } from '~utils/user';
import {
  getRouteScheduleRepitionDescriptionUnit,
  isRouteScheduleRepetionTypeWeekly,
} from '~utils/scheduleRoute/repetionType';
// eslint-disable-next-line max-len
import { buildScheduleRouteRepetionDescription } from '~utils/scheduleRoute/repetionDescription/builder';
import {
  SCHEDULE_ROUTE_REPETION_TYPE_OPTIONS,
  SCHEDULE_ROUTE_WEEKLY_DAYS_OPTIONS,
} from '~constants/scheduleRoute';

// eslint-disable-next-line max-len
import { AssistantGenerateRoutesDialogScheduleRouteCardFormToggleButtonGroup } from '../AssistantGenerateRoutes/Dialog/Components/ScheduleRouteCard/Form/styles';

import { DialogEditScheduleRouteContentProps } from './types';
import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  getLabelDriverOption,
  getDisabledDriverOption,
  getLabelVehicleOption,
  getDisabledVehicleOption,
  getUpdateScheduleRouteBody,
} from './utils';

const DialogEditScheduleRouteContent = ({
  scheduleRouteInfo,
  drivers,
  vehicles,
  onClose,
  onSuccessSubmit,
}: DialogEditScheduleRouteContentProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const formatterDateUtcOffset = useUtcOffset();

  const title = useMemo(() => {
    const scheduleRouteId = formatId(scheduleRouteInfo.routeScheduleId);

    const scheduleRouteTime = formatterDateUtcOffset(
      scheduleRouteInfo.apiTransactionScheduledDateTime,
      'HH:mm',
    );

    return `Editar agenda #${scheduleRouteId} (${scheduleRouteTime})`;
  }, [
    formatterDateUtcOffset,
    scheduleRouteInfo.routeScheduleId,
    scheduleRouteInfo.apiTransactionScheduledDateTime,
  ]);

  const defaultDriver = useMemo(
    () =>
      scheduleRouteInfo.driverId
        ? find(drivers, { id: scheduleRouteInfo.driverId as string })
        : undefined,
    [scheduleRouteInfo.driverId, drivers],
  );

  const defaultVehicle = useMemo(
    () => find(vehicles, { vehicleId: scheduleRouteInfo.vehicleId as string }),
    [scheduleRouteInfo.vehicleId, vehicles],
  );

  const [, loadingUpdateScheduleRoute, , executeGenerateUpdateScheduleRoute] =
    useLazyRequest({
      request: updateScheduleRoute,
      withPostSuccess: () => {
        const scheduleRouteId = scheduleRouteInfo.routeScheduleId;

        enqueueSnackbar(
          `La agenda ${scheduleRouteId} fue editada correctamente`,
          { variant: 'success' },
        );

        onSuccessSubmit?.();

        onClose();
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo editar la agenda, intente nuevamente', {
          variant: 'error',
        });
      },
    });

  const onSubmit = useCallback(
    async (
      formValues: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const body = getUpdateScheduleRouteBody({
        scheduleRouteId: scheduleRouteInfo.routeScheduleId,
        templateId: scheduleRouteInfo.routeTemplateId,
        startDate: scheduleRouteInfo.startDate,
        formValues,
        formatterDateUtcOffset,
      });

      await executeGenerateUpdateScheduleRoute(body);

      setSubmitting(false);
    },
    [
      executeGenerateUpdateScheduleRoute,
      formatterDateUtcOffset,
      scheduleRouteInfo.routeScheduleId,
      scheduleRouteInfo.routeTemplateId,
      scheduleRouteInfo.startDate,
    ],
  );

  const {
    values,
    errors,
    touched,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    submitForm,
    dirty,
    isValid,
    isSubmitting,
    validateForm,
  } = useFormik({
    initialValues: getInitialValues(scheduleRouteInfo),
    validationSchema,
    onSubmit,
  });

  const minDateToFieldEndDate = useMemo(
    () =>
      moment(scheduleRouteInfo.startDate, moment.HTML5_FMT.DATE)
        .startOf('day')
        .add(1, 'day')
        .toDate(),
    [scheduleRouteInfo.startDate],
  );

  const endDateValue = useMemo(() => values[FIELDS_NAME.END_DATE], [values]);

  const handleChangeEndDate = useCallback(
    (value: Date | null): void => {
      setFieldValue(FIELDS_NAME.END_DATE, value);
    },
    [setFieldValue],
  );

  const handleBlurEndDate = useCallback((): void => {
    setFieldTouched(FIELDS_NAME.END_DATE, true);
  }, [setFieldTouched]);

  const repetionTypeValue = useMemo(
    () => values[FIELDS_NAME.REPETION_TYPE],
    [values],
  );

  const handleChangeRepetionType = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setFieldValue(FIELDS_NAME.REPETION_TYPE, value, false);
      setFieldValue(FIELDS_NAME.REPETION_INTERVAL_DAYS, [], false);

      await validateForm();
    },
    [setFieldValue, validateForm],
  );

  const repetionIntervalCountValue = useMemo(
    () => values[FIELDS_NAME.REPETION_INTERVAL_COUNT],
    [values],
  );

  const reptionIntervalDaysValue = useMemo(
    () => values[FIELDS_NAME.REPETION_INTERVAL_DAYS],
    [values],
  );

  const handleChangescheduleReptionIntervalDays = useCallback(
    (
      _event: MouseEvent<HTMLElement>,
      value: ScheduleRouteWeeklyDayIndex[],
    ): void => {
      setFieldValue(FIELDS_NAME.REPETION_INTERVAL_DAYS, value);
    },
    [setFieldValue],
  );

  const handleBlurscheduleReptionIntervalDays = useCallback((): void => {
    setFieldTouched(FIELDS_NAME.REPETION_INTERVAL_DAYS, true);
  }, [setFieldTouched]);

  const resultDescription = useMemo(() => {
    let repetionDescription: undefined | string = undefined;

    if (repetionIntervalCountValue > 0) {
      repetionDescription = buildScheduleRouteRepetionDescription({
        repetitionTypeId: repetionTypeValue,
        repeatInterval: repetionIntervalCountValue,
        repetitionIntervalArray: reptionIntervalDaysValue,
        dayFormat: 'long',
      });

      if (
        isRouteScheduleRepetionTypeWeekly(repetionTypeValue) &&
        reptionIntervalDaysValue.length === 0
      ) {
        repetionDescription = undefined;
      }
    }

    let datesDescription: undefined | string = undefined;

    if (scheduleRouteInfo.startDate && endDateValue) {
      const startDateFormatter = formatterDate(scheduleRouteInfo.startDate, {
        format: 'DD/MM/YYYY',
      });

      const endDateFormatter = formatterDate(endDateValue, {
        format: 'DD/MM/YYYY',
      });

      datesDescription = `${startDateFormatter} hasta ${endDateFormatter}`;
    }

    if (repetionDescription && datesDescription) {
      return `Se produce ${lowerFirst(
        repetionDescription,
      )} empezando el ${datesDescription}`;
    }

    return undefined;
  }, [
    endDateValue,
    reptionIntervalDaysValue,
    repetionIntervalCountValue,
    repetionTypeValue,
    scheduleRouteInfo.startDate,
  ]);

  return (
    <>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <FormGroup row sx={{ alignItems: 'center' }}>
            <FormControlLabel
              label="Activada"
              control={
                <Switch
                  {...getFieldProps(FIELDS_NAME.ENABLED)}
                  checked={values[FIELDS_NAME.ENABLED]}
                />
              }
              sx={{ margin: 0 }}
            />
          </FormGroup>

          <TextField
            {...getFieldProps(FIELDS_NAME.TITLE)}
            label="Nombre"
            error={hasError(touched, errors, FIELDS_NAME.TITLE)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.TITLE)}
            fullWidth
            autoComplete="off"
          />

          <CustomAutocomplete
            options={drivers}
            fullWidth
            label="Asignación de chofer"
            name={FIELDS_NAME.DRIVER}
            defaultValue={defaultDriver}
            onChange={(_e, val) => {
              setFieldValue(FIELDS_NAME.DRIVER, val?.id ?? '');
            }}
            onBlur={() => {
              setFieldTouched(FIELDS_NAME.DRIVER, true);
            }}
            error={hasError(touched, errors, FIELDS_NAME.DRIVER)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.DRIVER)}
            getOptionLabel={getLabelDriverOption}
            getOptionDisabled={(option) =>
              getDisabledDriverOption(option, defaultDriver)
            }
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.id}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <UserInfo
                  name={option.displayName}
                  urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
                />

                <IsAvailableChip
                  isAvailable={option.isAvailable as boolean}
                  isAssigned={option.id === defaultDriver?.id}
                  size="small"
                  variant="filled"
                />
              </li>
            )}
            startAdornment={(val) =>
              val && (
                <Avatar
                  src={getDefaultAvatar(val.avatar ?? undefined)}
                  alt={val?.displayName}
                  sx={{ width: 20, height: 20 }}
                />
              )
            }
            filterOptions={createFilterOptions({
              ignoreAccents: true,
              ignoreCase: true,
              stringify: (option) =>
                getLabelDriverOption(option).replace(',', ''),
            })}
          />

          <CustomAutocomplete
            options={vehicles}
            fullWidth
            label="Asignación de vehículo"
            name={FIELDS_NAME.VEHICLE}
            disableClearable
            defaultValue={defaultVehicle}
            onChange={(_e, val) => {
              setFieldValue(FIELDS_NAME.VEHICLE, val.vehicleId ?? '');
            }}
            onBlur={() => {
              setFieldTouched(FIELDS_NAME.VEHICLE, true);
            }}
            error={hasError(touched, errors, FIELDS_NAME.VEHICLE)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.VEHICLE)}
            getOptionLabel={getLabelVehicleOption}
            getOptionDisabled={(option) =>
              getDisabledVehicleOption(option, defaultVehicle)
            }
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.vehicleId}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <VehicleInfo
                  vehicle={option.name}
                  vehicleCode={option.referenceCode ?? ''}
                  color={option.color}
                  useCaption={false}
                  withTooltip={false}
                />

                <IsAvailableChip
                  isAvailable={option.isAvailable}
                  isOpenForWork={true}
                  isAssigned={option.vehicleId === defaultVehicle?.vehicleId}
                  size="small"
                  variant="filled"
                />
              </li>
            )}
            startAdornment={(val) =>
              val && (
                <FontAwesomeIcon
                  icon={VehicleIcon}
                  style={{ color: val.color, width: 20, height: 20 }}
                />
              )
            }
            filterOptions={createFilterOptions({
              ignoreAccents: true,
              ignoreCase: true,
              stringify: (option) =>
                getLabelVehicleOption(option).replace(',', ''),
            })}
          />

          <DatePicker
            label="Fecha de finalización"
            value={endDateValue}
            onChange={handleChangeEndDate}
            minDate={minDateToFieldEndDate}
            renderInput={(props) => (
              <TextField
                {...props}
                onBlur={handleBlurEndDate}
                error={hasError(touched, errors, FIELDS_NAME.END_DATE)}
                helperText={getHelperText(
                  touched,
                  errors,
                  FIELDS_NAME.END_DATE,
                )}
                fullWidth
              />
            )}
          />

          <Stack spacing={2}>
            <Stack spacing={2} direction="row">
              <TextField
                {...getFieldProps(FIELDS_NAME.REPETION_TYPE)}
                label="Periodicidad"
                onChange={handleChangeRepetionType}
                error={hasError(touched, errors, FIELDS_NAME.REPETION_TYPE)}
                helperText={getHelperText(
                  touched,
                  errors,
                  FIELDS_NAME.REPETION_TYPE,
                )}
                select
                fullWidth
                autoComplete="off"
              >
                {SCHEDULE_ROUTE_REPETION_TYPE_OPTIONS.map((option) => (
                  <MenuItem
                    key={`schedule-route-repetion-type-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                {...getFieldProps(FIELDS_NAME.REPETION_INTERVAL_COUNT)}
                label="Cada"
                type="number"
                error={hasError(
                  touched,
                  errors,
                  FIELDS_NAME.REPETION_INTERVAL_COUNT,
                )}
                helperText={getHelperText(
                  touched,
                  errors,
                  FIELDS_NAME.REPETION_INTERVAL_COUNT,
                )}
                fullWidth
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {getRouteScheduleRepitionDescriptionUnit(
                        repetionTypeValue,
                        repetionIntervalCountValue,
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {isRouteScheduleRepetionTypeWeekly(repetionTypeValue) && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Días de repetición (selecciona qué día o días se va a
                  programar la ruta).
                </Typography>

                <Stack>
                  <AssistantGenerateRoutesDialogScheduleRouteCardFormToggleButtonGroup
                    value={reptionIntervalDaysValue}
                    onChange={handleChangescheduleReptionIntervalDays}
                    onBlur={handleBlurscheduleReptionIntervalDays}
                  >
                    {SCHEDULE_ROUTE_WEEKLY_DAYS_OPTIONS.map((option) => (
                      <ToggleButton
                        key={`schedule-route-weekly-days-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </ToggleButton>
                    ))}
                  </AssistantGenerateRoutesDialogScheduleRouteCardFormToggleButtonGroup>

                  <FormHelperText
                    error={hasError(
                      touched,
                      errors,
                      FIELDS_NAME.REPETION_INTERVAL_DAYS,
                    )}
                  >
                    {getHelperText(
                      touched,
                      errors,
                      FIELDS_NAME.REPETION_INTERVAL_DAYS,
                    )}
                  </FormHelperText>
                </Stack>
              </Stack>
            )}

            {resultDescription && (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Resultado de la programación
                </Typography>

                <Typography variant="body2" fontWeight="bold">
                  {resultDescription}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingUpdateScheduleRoute || isSubmitting}
          disabled={!dirty || !isValid}
        >
          Guardar cambios
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default DialogEditScheduleRouteContent;
